import type { ChangeEvent } from 'react';

import useFormContext from '@src/shared/form/useFormContext';
import Avatar from '@src/shared/ui/Avatar';
import { Button } from '@src/shared/ui/Button';
import { AccountBoxIcon, DeleteIcon, UploadIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';

const StyleSheet = {
  root: {
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: 'grey.200',
    borderRadius: 1,
    mr: 1.25,
    width: 40,
    height: 40,
  },
};

type FormContextShape = {
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
  watch: (name: string) => unknown;
};

function PhotoUploadField() {
  const { setValue, watch } = useFormContext() as FormContextShape;
  const photoPreview = watch('photoPreview') as string | null;

  const clearPhoto = () => {
    setValue('photoFile', null, { shouldDirty: true, shouldValidate: true });
    setValue('photoPreview', null, { shouldDirty: true, shouldValidate: true });
  };

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    setValue('photoFile', file, { shouldDirty: true, shouldValidate: true });

    if (!file) {
      setValue('photoPreview', null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue('photoPreview', typeof reader.result === 'string' ? reader.result : null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={StyleSheet.root}>
      <Avatar src={photoPreview ?? undefined} sx={StyleSheet.avatar}>
        <AccountBoxIcon />
      </Avatar>

      {photoPreview ? (
        <Button
          variant="outlined"
          onClick={clearPhoto}
          startIcon={<DeleteIcon color="inherit" />}
        >
          Delete Photo
        </Button>
      ) : (
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadIcon color="inherit" />}
        >
          Upload Photo
          <input hidden type="file" accept="image/*" onChange={handlePhotoChange} />
        </Button>
      )}
    </Box>
  );
}

export default PhotoUploadField;
